import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDateTime } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const PAGE = 50;

/** Who changed whose access, and when: role levels and limits, temporary access, cover, role and project changes. */
export default function AccessHistory() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get("/rbac/audit", { params: { limit: PAGE, offset, q: q.trim() || undefined } });
      setTotal(data.total);
      setRows((prev) => (offset ? [...prev, ...data.rows] : data.rows));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [q]);
  useEffect(() => { const t = setTimeout(() => load(0), 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-6" data-testid="access-history-page">
      <PageHeader title="Access history" description="Every change to who can do what: role levels and limits, temporary access, cover for someone, role and project changes." />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by person, role or module…" className="max-w-sm" data-testid="history-search" />
      <div className="rounded-xl border border-border bg-card">
        {rows.length === 0 && !loading && <p className="p-6 text-center text-sm text-muted-foreground" data-testid="history-empty">Nothing recorded yet.</p>}
        <ul className="divide-y divide-border/60">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:items-baseline sm:gap-4" data-testid="history-row">
              <span className="w-44 shrink-0 text-xs text-muted-foreground">{fmtDateTime(r.when)}</span>
              <span className="min-w-0 flex-1 text-sm text-foreground">{r.summary}</span>
              <span className="shrink-0 text-xs text-muted-foreground">by {r.who}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span data-testid="history-count">Showing {rows.length} of {total}</span>
        {rows.length < total && <Button variant="outline" size="sm" disabled={loading} onClick={() => load(rows.length)} data-testid="history-more">{loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Show more</Button>}
      </div>
    </div>
  );
}
